<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">المعلمين المسجلين</h1>
      </div>
    </div>

    <!-- Teachers Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">المعلمين</h1>
      </div>
      <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900">الاسم</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الايميل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الجوال</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">التعليم</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">المنطقة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">نسخة التطبيق</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">منصة التطبيق</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حالة المعلم</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تغير الرقم السري</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تغير بيانات المعلم</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(teacher, idx) in teachers" :key="teacher.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium text-gray-900">{{ teacher.fullName }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ teacher.email }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ teacher.phone }}</td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium text-gray-900">{{ teacher.qualificationValue }}</td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium text-gray-900">{{ teacher.regionDesc }}</td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-500">
                    <div v-if="teacher.appVersion">
                      <div>{{ teacher.appVersion }}</div>
                      <div v-if="teacher.appBuildNumber" class="text-xs text-gray-400">({{ teacher.appBuildNumber }})</div>
                    </div>
                    <span v-else class="text-gray-400">-</span>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-500">
                    <span v-if="teacher.appPlatform" class="font-medium">
                      {{ teacher.appPlatform === 'android' ? 'Android' : teacher.appPlatform === 'ios' ? 'iOS' : teacher.appPlatform }}
                    </span>
                    <span v-else class="text-gray-400">-</span>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium text-gray-900">
                    <select
                      v-model="teacher.enabled"
                      @change="submitStatus(idx)"
                      class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option :value="true">فعال</option>
                      <option :value="false">غير فعال</option>
                    </select>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium text-gray-900">
                    <button class="enabled-button" @click="openPopupPassword(idx)">تغير</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium text-gray-900">
                    <button class="enabled-button" @click="openPopupChangeDetails(idx)">تغير</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Password Popup -->
    <div v-if="showPopupPassword" class="popup" dir="rtl">
      <div class="popup-content">
        <h3 class="text-lg font-medium mb-4">تغيير الرقم السري</h3>
        <input
          v-model="password"
          type="password"
          placeholder="كلمة المرور الجديدة"
          class="w-full mb-2 p-2 border rounded"
          @input="validatePassword"
        />
        <input
          v-model="confirmPassword"
          type="password"
          placeholder="تأكيد كلمة المرور"
          class="w-full mb-2 p-2 border rounded"
          @input="validatePassword"
        />
        <span v-if="passwordError" class="text-red-500 text-xs">{{ passwordError }}</span>
        <div class="flex space-x-2 mt-4">
          <button @click="submitPassword" class="flex-1 bg-blue-600 text-white p-2 rounded">حفظ</button>
          <button @click="closePopup" class="flex-1 bg-gray-300 p-2 rounded">إلغاء</button>
        </div>
      </div>
    </div>

    <!-- Change Details Popup -->
    <div v-if="showPopupChangeDetails" class="popup" dir="rtl">
      <div class="popup-content">
        <h3 class="text-lg font-medium mb-4">تغيير بيانات المعلم</h3>

        <!-- Full Name -->
        <label class="block text-sm font-medium text-gray-700">الاسم</label>
        <input
          v-model="form.fullName"
          placeholder="الاسم الكامل"
          class="w-full mb-2 p-2 border rounded"
        />

        <!-- Email -->
        <label class="block text-sm font-medium text-gray-700">الايميل</label>
        <input
          v-model="form.email"
          placeholder="الايميل"
          @input="validateEmail"
          class="w-full mb-2 p-2 border rounded"
        />
        <span v-if="errors.email" class="text-red-500 text-xs">{{ errors.email }}</span>

        <!-- Phone -->
        <label class="block text-sm font-medium text-gray-700">الجوال</label>
        <div class="flex mb-2">
          <span class="px-3 bg-gray-200">+966</span>
          <input
            v-model="form.phone"
            placeholder="رقم الهاتف"
            maxlength="9"
            minlength="9"
            class="flex-1 p-2 border rounded-r"
          />
        </div>

        <!-- Qualification -->
        <label class="block text-sm font-medium text-gray-700">التعليم</label>
        <select
          v-model="form.qualificationId"
          class="w-full mb-2 p-2 border rounded"
        >
          <option
            v-for="q in qualifications"
            :key="q.id"
            :value="q.id"
          >{{ q.qualificationValue  }}</option>
        </select>

        <!-- National ID field removed as it's not essential for app functionality -->

        <!-- Region -->
        <label class="block text-sm font-medium text-gray-700">المنطقة</label>
        <select
          v-model="form.regionId"
          class="w-full mb-4 p-2 border rounded"
        >
          <option
            v-for="r in regions"
            :key="r.id"
            :value="r.id"
          >{{ r.regionNameAr  }}</option>
        </select>

        <div class="flex space-x-2">
          <button @click="submitDetails" class="flex-1 bg-blue-600 text-white p-2 rounded">حفظ</button>
          <button @click="closePopup" class="flex-1 bg-gray-300 p-2 rounded">إلغاء</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import url from '@/url';
import axios from 'axios';
import { defineComponent } from 'vue';

class TeacherDto {
  constructor(data) {
    this.id = data.id;
    this.fullName = data.fullName;
    this.email = data.email;
    this.phone = data.phone;
    this.qualificationValue = data.qualificationValue;
    this.qualificationId = data.qualificationId;
    // this.nationalId = data.nationalId; // Removed as it's not essential for app functionality
    this.regionDesc = data.regionDesc;
    this.regionId = data.regionId;
    this.enabled = data.enabled;
    this.appVersion = data.appVersion;
    this.appBuildNumber = data.appBuildNumber;
    this.appPlatform = data.appPlatform;
  }
}

export default defineComponent({
  name: 'Teachers',
  data() {
    return {
      teachers: [],
      // password popup
      showPopupPassword: false,
      password: '',
      confirmPassword: '',
      passwordError: '',
      // details popup
      showPopupChangeDetails: false,
      form: {
        fullName: '',
        email: '',
        phone: '',
        qualificationId: null,
        // nationalId: '', // Removed as it's not essential for app functionality
        regionId: null,
      },
      errors: { email: '' },
      qualifications: [],
      regions: [],
      // selection
      selectedTeacherIndex: null,
    };
  },
  async mounted() {
    await Promise.all([ this.loadDropdowns(), this.getAllTeachers() ]);
  },
  methods: {
    // load qualifications & regions
    async loadDropdowns() {
            const token = localStorage.getItem('token');
            const config = {
              headers: { 'X-Authorization': token }
            };
            
            // Get tenant_id from JWT token
            let tenantId = null;
            try {
              if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                tenantId = payload.tenantId || payload.tenant_id || null;
              }
            } catch (e) {
              console.warn('Could not extract tenantId from token:', e);
            }

            // Build URLs with tenantId as query parameter
            let qualificationsUrl = url.master_qualifications;
            if (tenantId) {
              qualificationsUrl += `?tenantId=${encodeURIComponent(tenantId)}`;
            }
            
      try {
        const [qRes, rRes] = await Promise.all([
          axios.get(process.env.VUE_APP_SERVICE_URL + qualificationsUrl ,config),
          axios.get(process.env.VUE_APP_SERVICE_URL + url.master_regions ,config)
        ]);
        this.qualifications = qRes.data.result.data;
        this.regions = rRes.data.result.data;
      } catch (e) {
        console.error(e);
      }
    },
    // fetch teachers list
    async getAllTeachers() {
      try {
        const res = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.get_all_teachers,
          { params: { isDeactivated: '0' }, headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        this.teachers = res.data.result.data.map(d => new TeacherDto(d));
      } catch (e) {
        console.error(e);
      }
    },
    // toggle enabled
    async submitStatus(idx) {
      const t = this.teachers[idx];
      try {
        await axios.put(
          `${process.env.VUE_APP_SERVICE_URL}${url.changeStatus}${t.id}?status=${t.enabled}`,
          {},
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        alert('تم تحديث حالة المعلم بنجاح!');
      } catch (e) {
        console.error(e);
        alert('فشل تحديث الحالة. حاول مرة أخرى.');
      }
    },
    // open password popup
    openPopupPassword(idx) {
      this.selectedTeacherIndex = idx;
      this.password = '';
      this.confirmPassword = '';
      this.passwordError = '';
      this.showPopupPassword = true;
    },
    // open details popup
    openPopupChangeDetails(idx) {
      this.selectedTeacherIndex = idx;
      const t = this.teachers[idx];
      this.form = {
        fullName:       t.fullName,
        email:          t.email,
        phone:          t.phone,
        qualificationId: t.qualificationId,
        // nationalId:     t.nationalId, // Removed as it's not essential for app functionality
        regionId:       t.regionId
      };
      this.errors.email = '';
      this.showPopupChangeDetails = true;
    },
    // close any popup
    closePopup() {
      this.showPopupPassword = false;
      this.showPopupChangeDetails = false;
    },
    // change password
    async submitPassword() {
      if (this.password !== this.confirmPassword) {
        this.passwordError = 'كلمات المرور غير متطابقة';
        return;
      }
      const t = this.teachers[this.selectedTeacherIndex];
      try {
        await axios.put(
          `${process.env.VUE_APP_SERVICE_URL}${url.changePassword}${t.id}?newPassword=${btoa(this.password)}`,
          {},
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        alert('تم تغيير الرقم السري بنجاح!');
        this.closePopup();
      } catch (e) {
        console.error(e);
      }
    },
    // save edited details
    async submitDetails() {
      if (this.errors.email) return;
      const t = this.teachers[this.selectedTeacherIndex];
      try {
        await axios.put(
          `${process.env.VUE_APP_SERVICE_URL + url.change_teacher_info + t.id}`,
          this.form,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        );
        alert('تم حفظ البيانات بنجاح!');
        this.closePopup();
        await this.getAllTeachers();
      } catch (e) {
        console.error(e);
        alert('فشل حفظ البيانات. حاول مرة أخرى.');
      }
    },
    // validations
    validatePassword() {
      const rules = [/[A-Z]/, /[a-z]/, /[0-9]/, /[@#!]/];
      this.passwordError = rules.every(r=>r.test(this.password)) ? '' : 'كلمة مرور غير صالحة';
    },
    validateEmail() {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      this.errors.email = re.test(this.form.email) ? '' : 'بريد إلكتروني غير صالح';
    }
  }
});
</script>

<style scoped>
.enabled-button {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor w-20 h-10;
}
.popup {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}
.popup-content {
  background: #fff;
  padding: 20px;
  border-radius: 5px;
  width: 580px;
  max-height: 90vh;
  overflow-y: auto;
}
</style>
